import React, { useMemo } from "react"

import { type Theme, css } from "@emotion/react"
import type { Interpolation } from "@emotion/serialize"
import useOnclickOutside from "react-cool-onclickoutside"

import { NIRVANA_COLORS, NIRVANA_THEME } from "../config/theme.config.ts"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"

export const PopupWrapperElement: React.FunctionComponent<
    IPopupProps
> = props => {
    const popupRef = useOnclickOutside(() => {
        if (props.isActive && props.closeOnOutsideClick) {
            props.onDone()
        }
    })

    const popupZIndex = useMemo<number>(() => {
        return (
            props.zIndex ||
            (props.increasedZIndex
                ? NIRVANA_THEME.popupIncreasedZIndex
                : NIRVANA_THEME.popupDefaultZIndex)
        )
    }, [props.zIndex, props.increasedZIndex])

    return props.isActive ? (
        <div
            className={`
                relative no-body-scroll
                ${props.className}
            `}
        >
            {/*OVERLAY*/}
            {!props.ignoreOverlay && (
                <div
                    css={[
                        styles.overlay,
                        props.overlayZIndex !== undefined &&
                            css({ zIndex: props.overlayZIndex }),
                    ]}
                />
            )}

            {/*POPUP*/}
            <div
                ref={popupRef}
                css={[
                    styles.popup,
                    css({
                        zIndex: popupZIndex,
                    }),
                    !props.popupWidthClassName
                        ? styles.popupDefaultWidth
                        : undefined,
                    props.popupCss,
                ]}
                className={`${props.popupWidthClassName} shadow-10 screen-center`}
            >
                {props.withCloseIcon ? (
                    <div
                        css={[
                            styles.closeIcon,
                            css({
                                zIndex: popupZIndex,
                            }),
                        ]}
                        onClick={props.onDone}
                    >
                        <img src={closeIcon} alt="Close" />
                    </div>
                ) : null}

                {props.children}
            </div>
        </div>
    ) : null
}

const styles = {
    overlay: css({
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 50,
        width: "100vw",
        height: "100vh",
        backgroundColor: NIRVANA_COLORS.brand.offWhite,
        opacity: 0.7,
    }),
    popup: css({
        position: "fixed",
        backgroundColor: NIRVANA_COLORS.brand.offWhite,
        borderRadius: 16,
        paddingTop: 40,
        paddingRight: 48,
        paddingBottom: 40,
        paddingLeft: 48,
        maxHeight: "100vh",
        overflowY: "auto",

        [NIRVANA_THEME.mediaQueries.md]: {
            width: "95vw",
            paddingTop: 32,
            paddingRight: 32,
            paddingBottom: 32,
            paddingLeft: 32,
        },
    }),
    popupDefaultWidth: css({
        width: 440,
    }),
    closeIcon: css({
        position: "absolute",
        right: 16,
        top: 16,
        padding: 10,
        cursor: "pointer",
    }),
}

interface IPopupProps {
    children?: React.ReactNode
    className?: string
    popupCss?: Interpolation<Theme>
    popupWidthClassName?: string
    isActive: boolean
    closeOnOutsideClick?: boolean
    onDone: (data?: any) => void
    increasedZIndex?: boolean
    ignoreOverlay?: boolean
    zIndex?: number
    overlayZIndex?: number
    withCloseIcon?: boolean
}
